import { DataScopeType } from '../../api/roles';

/** Một loại phạm vi dữ liệu, kèm mô tả và trạng thái cài đặt. */
export interface DataScopeOption {
  readonly value: number;
  readonly label: string;

  /** Giải thích phạm vi này cho ai và cho thấy những dòng nào. */
  readonly description: string;

  /**
   * Phạm vi này đã được cài đặt thật ở máy chủ.
   *
   * Phạm vi chưa cài đặt vẫn lưu được vào cơ sở dữ liệu — thực thể không chặn — nhưng phần lọc dữ liệu
   * chưa biết dịch nó, nên nó **không có tác dụng gì**. Với dữ liệu khách hàng, hệ quả là người giữ vai
   * trò đó không thấy dòng nào, và họ sẽ báo lỗi mà không ai đoán được nguyên nhân.
   *
   * Vì vậy giao diện chặn hẳn những phạm vi chưa cài đặt thay vì chỉ ghi chú. Đây là chỗ thiếu đã biết,
   * và nó sẽ mở dần theo từng module: phạm vi theo dự án mở cùng module dự án, phạm vi theo phòng ban
   * mở khi cây phòng ban được dùng thật.
   */
  readonly isImplemented: boolean;
}

/**
 * Toàn bộ loại phạm vi dữ liệu theo thiết kế, kèm trạng thái cài đặt.
 *
 * Liệt kê **cả những phạm vi chưa cài đặt** thay vì ẩn chúng đi. Lý do: một quy tắc đã khai bằng câu
 * lệnh SQL trước đây có thể đang dùng một trong số đó, và nếu danh sách này thiếu thì màn hình sẽ hiện
 * *"phạm vi lạ (số)"* — người quản trị không biết đó là gì để xóa.
 */
export const dataScopeOptions: readonly DataScopeOption[] = [
  {
    value: DataScopeType.AllCompanies,
    label: 'Mọi pháp nhân',
    description:
      'Thấy dữ liệu của mọi pháp nhân. Dành cho quản trị hệ thống. '
      + 'Hiện công ty chỉ có một pháp nhân nên nó tương đương phạm vi toàn pháp nhân.',
    isImplemented: true,
  },
  {
    value: DataScopeType.CurrentCompany,
    label: 'Toàn bộ pháp nhân đang làm việc',
    description:
      'Thấy mọi dòng dữ liệu của pháp nhân. Dành cho Giám đốc và Kế toán trưởng. '
      + 'Đây là phạm vi rộng nhất dùng cho người làm nghiệp vụ.',
    isImplemented: true,
  },
  {
    value: DataScopeType.AssignedCustomers,
    label: 'Chỉ khách hàng được phân công',
    description:
      'Chỉ thấy khách hàng có người phụ trách là chính mình. Dành cho nhân viên kinh doanh. '
      + 'Khách hàng do người khác phụ trách không xuất hiện trong danh sách và không mở được.',
    isImplemented: true,
  },
  {
    value: DataScopeType.Branch,
    label: 'Địa điểm của người dùng',
    description: 'Chưa cài đặt: phần lọc theo địa điểm sẽ có cùng lúc với module kho.',
    isImplemented: false,
  },
  {
    value: DataScopeType.OwnDepartment,
    label: 'Chỉ phòng ban của mình',
    description: 'Chưa cài đặt: sẽ mở khi cây phòng ban được dùng thật trong nghiệp vụ.',
    isImplemented: false,
  },
  {
    value: DataScopeType.OwnDepartmentAndBelow,
    label: 'Phòng ban của mình và cấp dưới',
    description: 'Chưa cài đặt: sẽ mở khi cây phòng ban được dùng thật trong nghiệp vụ.',
    isImplemented: false,
  },
  {
    value: DataScopeType.ProjectMember,
    label: 'Chỉ dự án mình tham gia',
    description:
      'Áp cho tài nguyên dự án. Gồm cả những dự án người đó đã rời — họ vẫn cần tra lại việc mình '
      + 'đã làm, nhưng không nhận thông báo và không được phân việc mới.',
    isImplemented: true,
  },
  {
    value: DataScopeType.ManagedProjects,
    label: 'Chỉ dự án mình quản lý',
    /*
      Câu cũ ở đây gợi ý "cấp cả hai loại thì thấy hợp của hai tập". Sai: mỗi tài nguyên của một vai
      trò chỉ có MỘT quy tắc phạm vi — khác loại phạm vi cũng tính là trùng — nên không cấp được cả
      hai loại cho cùng một tài nguyên. Muốn rộng hơn thì ghép quy tắc "*" với quy tắc của tài nguyên
      cụ thể: hai quy tắc khác tên tài nguyên thì cộng vào nhau.
    */
    description:
      'Áp cho tài nguyên dự án. Hẹp hơn phạm vi theo thành viên: chỉ những dự án người đó đứng tên '
      + 'quản lý. Mỗi tài nguyên chỉ có một quy tắc, nên với cùng một tài nguyên hãy chọn một trong '
      + 'hai loại.',
    isImplemented: true,
  },
  {
    value: DataScopeType.OwnRecordsOnly,
    label: 'Chỉ bản ghi của chính mình',
    description: 'Chưa cài đặt: sẽ có cùng lúc với chấm công và phiếu lương.',
    isImplemented: false,
  },
  {
    value: DataScopeType.OwnOrAssignedRecords,
    label: 'Do chính mình lập hoặc được giao cho mình',
    description:
      'Áp cho tài nguyên yêu cầu công việc. Dành cho loại dữ liệu có hai bên cần thấy nó: người '
      + 'đặt việc và người làm việc. Gồm cả người hỗ trợ, không chỉ người chịu trách nhiệm chính. '
      + 'Mỗi tài nguyên chỉ có một quy tắc; muốn rộng hơn thì giữ quy tắc "*" (ví dụ dự án mình '
      + 'tham gia) và lập thêm quy tắc này cho yêu cầu công việc — hai quy tắc cộng vào nhau.',
    isImplemented: true,
  },
];

/** Tên hiển thị của một loại phạm vi. */
export function dataScopeLabel(scopeType: number): string {
  return (
    dataScopeOptions.find((option) => option.value === scopeType)?.label
    ?? `Phạm vi lạ (${String(scopeType)})`
  );
}

/** Phạm vi này đã cài đặt thật hay chưa. */
export function isDataScopeImplemented(scopeType: number): boolean {
  return dataScopeOptions.find((option) => option.value === scopeType)?.isImplemented ?? false;
}

/**
 * Tên tài nguyên khai được trong quy tắc phạm vi.
 *
 * Danh sách này phải khớp với những tài nguyên mà máy chủ **thật sự tra phạm vi**. Khai một tên tài
 * nguyên không có nơi nào tra thì quy tắc nằm im trong bảng và không có tác dụng — cùng kiểu sai lệch
 * với việc khai một phạm vi chưa cài đặt.
 *
 * Dấu sao nghĩa là mọi tài nguyên; nó luôn dùng được vì phép so khớp tài nguyên nằm ở tầng chung.
 */
export const resourceNameOptions: readonly { readonly value: string; readonly label: string }[] = [
  { value: '*', label: 'Mọi tài nguyên (*)' },
  { value: 'customer', label: 'Khách hàng (customer)' },

  /*
    Dự án bị thiếu ở đây từ lúc danh sách này được dựng, dù máy chủ vẫn tra phạm vi cho nó.
    Hệ quả: không ai lập được quy tắc phạm vi cho dự án từ giao diện. Không ai gặp vì bộ dựng dự
    liệu nền tự lập sẵn quy tắc cho hai vai trò dựng sẵn — nhưng vai trò khai tay thì bị chặn.

    Kiểu lệch này nay đã có phép kiểm chặn: npm run check:data-scopes.
  */
  { value: 'project', label: 'Dự án (project)' },
  { value: 'work_request', label: 'Yêu cầu công việc (work_request)' },
];

/**
 * Những tài nguyên còn lập được quy tắc mới cho một vai trò.
 *
 * Mỗi vai trò chỉ có **một** quy tắc phạm vi cho mỗi tài nguyên — khác loại phạm vi cũng tính là
 * trùng, máy chủ từ chối. Nên tài nguyên đã có quy tắc bị loại khỏi danh sách thay vì để người quản
 * trị chọn rồi nhận lỗi. Muốn đổi loại phạm vi của một tài nguyên thì xóa quy tắc cũ rồi lập lại.
 *
 * @param existingResourceNames Tên tài nguyên của những quy tắc vai trò đang có.
 */
export function availableResourceNameOptions(
  existingResourceNames: readonly string[],
): readonly { readonly value: string; readonly label: string }[] {
  const used = new Set(existingResourceNames.map((name) => name.trim()));

  return resourceNameOptions.filter((option) => !used.has(option.value));
}
